<template>
  <div v-if="!!question && !!options">
    <h6>{{ label }}</h6>
    <div class="mb-2" v-html="question.description"></div>

    <div v-for="(child, index) in children" :key="index">
      <b-row v-if="options.name">
        <b-col class="col-5 pr-0">
          <b-form-group :label="trans('first_name') + (options.name == 'required' ? '*' : '')">
            <b-form-input
              :size="size"
              :name="`${slug}.${index}.first_name`"
              v-model="children[index].first_name"
              :state="isFirstNameValid(child, index)"
              required
              :disabled="disabled"
              @focus="$emit('clear-error', `${slug}.${index}.first_name`)"
            />
            <b-form-invalid-feedback :state="errors[index] && errors[index].first_name ? false : null">
              {{ errors[index] ? (errors[index].first_name||[]).join(' ') : '' }}
            </b-form-invalid-feedback>
          </b-form-group>
        </b-col>
        <b-col class="col-5">
          <b-form-group :label="trans('last_name') + (options.name == 'required' ? '*' : '')">
            <b-form-input
              :size="size"
              :name="`${slug}.${index}.last_name`"
              v-model="children[index].last_name"
              :state="isLastNameValid(child, index)"
              required
              :disabled="disabled"
              @focus="$emit('clear-error', `${slug}.${index}.last_name`)"
            />
            <b-form-invalid-feedback :state="errors[index] && errors[index].last_name ? false : null">
              {{ errors[index] ? (errors[index].last_name||[]).join(' ') : '' }}
            </b-form-invalid-feedback>
          </b-form-group>
        </b-col>
        <b-col v-if="!question.required || index >= 1" class="col-2">
          <b-form-group class="mb-0">
            <label>&nbsp;</label>
          </b-form-group>
          <b-button variant="link" class="text-danger" @click="onClickRemove(index)">
            <i class="fa fa-fw fa-times"></i>
          </b-button>
        </b-col>
      </b-row>

      <b-row v-if="options.gender || options.date_of_birth">
        <b-col class="col-5 pr-0" v-if="options.gender">
          <b-form-group :label="trans('gender') + (options.gender == 'required' ? '*' : '')">
            <b-form-select
              :size="size"
              :name="`${slug}.${index}.gender`"
              v-model="children[index].gender"
              :state="isGenderValid(child, index)"
              :required="options.gender == 'required'"
              :options="genderOptions"
              :disabled="disabled"
              @change="$emit('clear-error', `${slug}.${index}.gender`)"
            />
            <b-form-invalid-feedback :state="errors[index] && errors[index].gender ? false : null">
              {{ errors[index] ? (errors[index].gender||[]).join(' ') : '' }}
            </b-form-invalid-feedback>
          </b-form-group>
        </b-col>
        <b-col class="col-5" v-if="options.date_of_birth">
          <b-form-group :label="trans('date_of_birth') + (options.date_of_birth == 'required' ? '*' : '')">
            <datetime
              v-model="children[index].date_of_birth"
              :name="`${slug}.${index}.date_of_birth`"
              :config="dateOptions"
              :state="isDateOfBirthValid(child, index)"
              :disabled="disabled"
              :class="`form-control form-control-${size} ${isDateOfBirthValid(child, index) === true ? 'is-valid' : (isDateOfBirthValid(child, index) === false ? 'is-invalid' : '')}`"
              @dp-show="$emit('clear-error', `${slug}.${index}.date_of_birth`)"
            />
            <b-form-text v-if="options.date_of_birth == 'required' && printAgeConstraint(ageConstraint)">
              <span :class="doesDateOfBirthRespectAgeConstraint(child, index) === false ? 'text-danger' : ''">{{ printAgeConstraint(ageConstraint) }}</span>
            </b-form-text>
            <b-form-invalid-feedback :state="errors[index] && errors[index].date_of_birth ? false : null">
              {{ errors[index] ? (errors[index].date_of_birth||[]).join(' ') : '' }}
            </b-form-invalid-feedback>
          </b-form-group>
        </b-col>
      </b-row>

      <b-row v-if="options.grade || options.friend">
        <b-col class="col-5 pr-0" v-if="options.grade">
          <b-form-group :label="trans('grade') + (required && options.grade == 'required' ? '*' : '')">
            <b-form-input
              v-if="!options.grade_choices || options.grade_choices.length == 0"
              :size="size"
              :name="`${slug}.${index}.grade`"
              v-model="children[index].grade"
              :state="isGradeValid(child, index)"
              :required="options.grade == 'required'"
              :disabled="disabled"
              @focus="$emit('clear-error', `${slug}.${index}.grade`)"
            />
            <b-form-select
              v-if="!!options.grade_choices && options.grade_choices.length > 0"
              :size="size"
              :name="`${slug}.${index}.grade`"
              v-model="children[index].grade"
              :options="gradeOptions"
              :state="isGradeValid(child, index)"
              :required="options.grade == 'required'"
              :disabled="disabled"
              @change="$emit('clear-error', `${slug}.${index}.grade`)"
            />
            <b-form-invalid-feedback :state="errors[index] && errors[index].grade ? false : null">
              {{ errors[index] ? (errors[index].grade||[]).join(' ') : '' }}
            </b-form-invalid-feedback>
          </b-form-group>
        </b-col>
        <b-col class="col-5 pr-0" v-if="options.friend">
          <b-form-group :label="trans('friend_pairing') + (required && options.friend == 'required' ? '*' : '')">
            <b-form-input
              :size="size"
              :name="`${slug}.${index}.friend`"
              v-model="children[index].friend"
              :state="isFriendValid(child, index)"
              :required="options.friend == 'required'"
              :disabled="disabled"
              @focus="$emit('clear-error', `${slug}.${index}.friend`)"
            />
            <b-form-invalid-feedback :state="errors[index] && errors[index].friend ? false : null">
              {{ errors[index] ? (errors[index].friend||[]).join(' ') : '' }}
            </b-form-invalid-feedback>
          </b-form-group>
        </b-col>
      </b-row>
      <b-row v-if="options.pickup_password">
        <b-col class="col-5 pr-0">
          <b-form-group :label="trans('pickup_password') + (required && options.pickup_password == 'required' ? '*' : '')">
            <b-form-input
              :size="size"
              :name="`${slug}.pickup_password`"
              v-model="children[index].pickup_password"
              :state="isPickupPasswordValid(child, index)"
              :required="options.pickup_password == 'required'"
              :disabled="disabled"
              @focus="$emit('clear-error', `${slug}.${index}.pickup_password`)"
            />
            <b-form-invalid-feedback :state="errors[index] && errors[index].pickup_password ? false : null">
              {{ errors[index] ? (errors[index].pickup_password||[]).join(' ') : '' }}
            </b-form-invalid-feedback>
          </b-form-group>
        </b-col>
      </b-row>

      <b-row v-if="options.email || options.mobile">
        <b-col v-if="options.email" :class="`col-5 ${options.mobile ? 'pr-0' : ''}`">
          <b-form-group :label="trans('email') + (options.email == 'required' ? '*' : '')">
            <b-form-input
              :size="size"
              :name="`${slug}.${index}.email`"
              type="email"
              v-model="children[index].email"
              :state="isEmailValid(child, index)"
              :required="options.email == 'required'"
              :disabled="disabled"
              @focus="$emit('clear-error', `${slug}.${index}.email`)"
            />
            <b-form-invalid-feedback :state="errors[index] && !!errors[index].email ? false : null">
              {{ errors[index] ? (errors[index].email||[]).join(' ') : '' }}
            </b-form-invalid-feedback>
          </b-form-group>
        </b-col>
        <b-col v-if="options.mobile" class="col-5">
          <b-form-group :label="trans('mobile') + (options.mobile == 'required' ? '*' : '')">
            <b-form-input
              :size="size"
              :name="`${slug}.${index}.mobile`"
              v-model="children[index].mobile"
              :state="isMobileValid(child, index)"
              :formatter="phoneFormatter"
              :required="options.mobile == 'required'"
              :disabled="disabled"
              @focus="$emit('clear-error', `${slug}.${index}.mobile`)"
            />
            <b-form-invalid-feedback :state="errors[index] && !!errors[index].mobile ? false : null">
              {{ errors[index] ? (errors[index].mobile||[]).join(' ') : '' }}
            </b-form-invalid-feedback>
          </b-form-group>
        </b-col>
      </b-row>
    </div>
    <b-form-group v-if="options.multiple">
      <b-button variant="link" size="sm" @click="onClickAdd">
        <i class="fa fa-fw fa-plus"></i>
        {{ trans('add') }}
      </b-button>
    </b-form-group>

  </div>
</template>

<script>
  import Datetime from 'vue-bootstrap-datetimepicker'

  function isEmpty(value) {
    return ((value||'')+'').trim().length == 0
  }

  const blank = {
    first_name: null,
    last_name: null,
    name: null,
    gender: null,
    date_of_birth: null,
    grade: null,
    friend: null,
    pickup_password: null,
    email: null,
    mobile: null,
  }

  export default {
    props: {
      value: Array, // answer
      question: Object,
      disabled: {
        type: Boolean,
        default: false,
      },
      size: {
        type: String,
        default: 'md',
      },
      errors: {
        type: Array,
        default() {
          return []
        },
      },
    },
    components: {
      Datetime,
    },
    data() {
      return {
        trans: trans,
        printAgeConstraint: _.printAgeConstraint,
        moment: moment,
        children: _.cloneDeep(this.value && this.value.length ? this.value : [blank]),
        dateOptions: {
          locale: window.lang,
          format: 'LL',
          viewMode: 'years',
          showClear: true,
        },
      }
    },
    computed: {
      genderOptions() {
        let choices = _.clone(this.question.options.gender_choices || _.allGenderChoices)
        if (this.question.options.gender != 'required') choices.unshift(null)
        return _.map(choices, gender => {
          return {
            value: gender,
            text: gender ? trans('gender_' + gender) : ''
          }
        })
      },
      ageConstraint() {
        return this.question && this.question.options && this.question.options.age_constraint ? 
          this.question.options.age_constraint :
          null
      },
      gradeOptions() {
        let choices = !!this.question.options.grade_choices ?
          _.compact(this.question.options.grade_choices) :
          []
        if (this.question.options.grade != 'required') choices.unshift(null)
        return _.map(choices, grade => {
          return {
            value: grade,
            text: grade || '',
          }
        })
      },
      options() {
        return this.question.options
      },
      label() {
        return this.question.label
      },
      slug() {
        return this.question.slug
      },
      required() {
        return this.question.required
      },
      description() {
        return this.question.description
      },
    },
    watch: {
      children: {
        deep: true,
        handler() {
          let children = this.serialize()
          if (!_.isEqual(this.value, children)) this.$emit('input', children)
        },
      },
      value() {this.hydrate()},
    },
    methods: {
      onClickAdd() {
        this.children.push(_.clone(blank))
      },
      onClickRemove(index) {
        this.children.splice(index, 1)
      },
      isFirstNameValid(child, index) {
        if (this.errors[index] && this.errors[index].first_name) return false
        if (child.first_name) return true
        return null
      },
      isLastNameValid(child, index) {
        if (this.errors[index] && this.errors[index].last_name) return false
        if (child.last_name) return true
        return null
      },
      isGenderValid(child, index) {
        if (this.errors[index] && this.errors[index].gender) return false
        if (child.gender) return true
        return null
      },
      doesDateOfBirthRespectAgeConstraint(child, index) {
        return _.validateAgeConstraint(moment(child.date_of_birth, 'LL').toISOString(), this.ageConstraint)
      },
      isDateOfBirthValid(child, index) {
        if (this.errors[index] && this.errors[index].date_of_birth) return false
        if (isEmpty(child.date_of_birth)) return null
        if (this.doesDateOfBirthRespectAgeConstraint(child, index) === false) return false
        return !isEmpty(child.date_of_birth) && moment(child.date_of_birth, 'LL').diff(moment(), 'days') < 0
      },
      isGradeValid(child, index) {
        if (this.errors[index] && this.errors[index].grade) return false
        if (child.grade) return true
        return null
      },
      isPickupPasswordValid(child, index) {
        if (this.errors[index] && this.errors[index].pickup_password) return false
        if (child.pickup_password) return true
        return null
      },
      isFriendValid(child, index) {
        if (this.errors[index] && this.errors[index].friend) return false
        if (child.friend) return true
        return null
      },
      isEmailValid(child, index) {
        if (this.errors[index] && this.errors[index].email) return false
        if (isEmpty(child.email)) return null
        return _.validateEmail(child.email)
      },
      isMobileValid(child, index) {
        if (this.errors[index] && this.errors[index].mobile) return false
        if (isEmpty(child.mobile)) return null
        return _.validatePhone(child.mobile)
      },
      phoneFormatter(phone) {
        return _.formatPhone(phone)
      },
      serialize() {
        return _.map(this.children, child => {
          return {
            first_name: child.first_name,
            last_name: child.last_name,
            name: [child.first_name, child.last_name].join(' '),
            gender: child.gender,
            date_of_birth: child.date_of_birth ? moment(child.date_of_birth, 'LL').format('YYYY-MM-DD') : null,
            grade: child.grade,
            friend: child.friend,
            pickup_password: child.pickup_password,
            email: child.email,
            mobile: child.mobile,
          }
        })
      },
      hydrate() {
        this.children = _.map(this.value && this.value.length ? this.value : (this.question.required || !this.value ? [blank] : []), child => {
          return {
            first_name: child.first_name,
            last_name: child.last_name,
            name: child.name,
            gender: child.gender,
            date_of_birth: child.date_of_birth ? moment(child.date_of_birth).format('LL') : null,
            grade: child.grade || null,
            friend: child.friend || null,
            pickup_password: child.pickup_password || null,
            email: child.email || null,
            mobile: child.mobile || null,
          }
        })
      },
    },
    mounted() {
      this.hydrate()
      window['formQuestion_'+this.question.slug] = this
    },
  }
</script>